// App chrome: top bar (Arlo, clock, who's logging, sync, emergency) and
// navigation — a side rail on tablets, a bottom bar on phones.

import type { LucideIcon } from 'lucide-react';
import {
  Images,
  BookOpen,
  CalendarHeart,
  Cctv,
  Check,
  Pin,
  MessageCircleQuestion,
  Footprints,
  HeartHandshake,
  ChartColumn,
  Cloud,
  CloudOff,
  House,
  HeartPulse,
  GraduationCap,
  List,
  Menu,
  Mic,
  Music2,
  Power,
  RefreshCw,
  Settings,
  ShoppingBasket,
  Siren,
  Compass,
  Utensils,
  Wallet,
  Smartphone,
  Sprout,
} from 'lucide-react';
import { Fragment, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { openVoice } from '../features/voice/VoiceAssistant.tsx';
import { isSession, setDevicePrefs } from '../data/device.ts';
import { api, checkKey } from '../data/api.ts';
import { reconnect } from '../data/index.ts';
import { canGetApp } from '../data/androidApp.ts';
import { AppUpdate } from './AppUpdate.tsx';
import { GoogleSignIn } from '../features/settings/GoogleSignIn.tsx';
import { useRole } from '../data/role.ts';
import { useCollection, useDevice, useHousehold, useLiveStatus, useNow, useProfile, useSyncState } from '../data/hooks.ts';
import { stageFor } from '../domain/age.ts';
import { Avatar } from '../ui/bits.tsx';
import { useWeather } from '../data/live.ts';
import { describeCode } from '../domain/weather.ts';
import { Sheet } from '../ui/primitives.tsx';
import { navigate, useRoute } from './router.ts';
import { reloadToLatest, useServerBuild } from './build.ts';
import { setScreenOff } from './kiosk.ts';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

export const NAV: NavItem[] = [
  { path: '/', label: 'Today', icon: House },
  { path: '/timeline', label: 'Timeline', icon: List },
  { path: '/walks', label: 'Walks', icon: Footprints },
  { path: '/insights', label: 'Insights', icon: ChartColumn },
  { path: '/diary', label: 'Photos', icon: Images },
  { path: '/health', label: 'Health', icon: HeartPulse },
  { path: '/food', label: 'Food', icon: Utensils },
  { path: '/training', label: 'Training', icon: GraduationCap },
  { path: '/social', label: 'Socialise', icon: Compass },
  { path: '/ask', label: 'Ask', icon: MessageCircleQuestion },
  { path: '/camera', label: 'Camera', icon: Cctv },
  { path: '/music', label: 'Music', icon: Music2 },
  { path: '/guide', label: 'Guide', icon: BookOpen },
  { path: '/growing', label: 'Growing up', icon: Sprout },
  { path: '/shopping', label: 'Shopping', icon: ShoppingBasket },
  { path: '/homecoming', label: 'Homecoming', icon: CalendarHeart },
  { path: '/costs', label: 'Costs', icon: Wallet },
  { path: '/settings', label: 'Settings', icon: Settings },
];

// Phone bottom bar: two items, the big mic, one item, More. What's useful
// changes when he comes home — before, it's getting ready; after, his day.
// Anyone can pin their own three instead (More → Edit bar).
const PHONE_NAV = ['/', '/walks', '/health'];
const PHONE_NAV_PREP = ['/', '/shopping', '/training'];
export const BAR_SIZE = 3;

// Tablet rail: the everyday ones either side of the Ask orb; the rest live under More.
const RAIL_PRIMARY = ['/', '/timeline', '/walks', '/health', '/food', '/training', '/camera'];
const RAIL_PREP = ['/', '/homecoming', '/shopping', '/health', '/food', '/training', '/guide'];

// A sitter's device: the essentials only.
const SITTER_ITEM: NavItem = { path: '/sitter', label: 'Sitter guide', icon: HeartHandshake };
const SITTER_NAV = ['/', '/sitter', '/timeline', '/walks', '/camera', '/music', '/ask', '/diary', '/guide', '/growing'];
const SITTER_PHONE = ['/', '/sitter', '/camera'];

function useNav() {
  const d = useDevice();
  const { isHome } = useLiveStatus(60_000);
  if (!d.guest) {
    const own = d.tabs?.filter((t) => t !== '/ask' && NAV.some((n) => n.path === t));
    return { nav: NAV, phone: own?.length === BAR_SIZE ? own : isHome ? PHONE_NAV : PHONE_NAV_PREP, rail: isHome ? RAIL_PRIMARY : RAIL_PREP, custom: own?.length === BAR_SIZE };
  }
  const nav = [NAV[0], SITTER_ITEM, ...NAV.slice(1)].filter((n) => SITTER_NAV.includes(n.path));
  // A sitter's list is short enough to show in full.
  return { nav, phone: SITTER_PHONE, rail: nav.map((n) => n.path), custom: false };
}

const phoneQuery = '(max-width: 699px)';
function useIsPhone(): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = matchMedia(phoneQuery);
      m.addEventListener('change', l);
      return () => m.removeEventListener('change', l);
    },
    () => matchMedia(phoneQuery).matches,
  );
}

function isActive(item: string, path: string) {
  return item === '/' ? path === '/' : path === item || path.startsWith(item + '/');
}

export function Shell({ children }: { children: ReactNode }) {
  const route = useRoute();
  const [more, setMore] = useState(false);
  const [editing, setEditing] = useState(false);
  const d = useDevice();
  const { nav: NAV, phone: PHONE_NAV, rail: RAIL, custom } = useNav();
  const isPhone = useIsPhone();
  useRole();
  // Tablet/desktop rail: Ask becomes the big orb in the middle, like the phone's bottom bar.
  const railItems = RAIL.flatMap((p) => NAV.filter((n) => n.path === p && p !== '/ask'));
  const railMid = Math.floor(railItems.length / 2);
  const railMore = NAV.filter((n) => n.path !== '/ask' && !RAIL.includes(n.path));
  // What More holds depends on which bar opened it.
  const shown = [...(isPhone ? PHONE_NAV : RAIL), '/ask'];
  const moreItems = NAV.filter((n) => !shown.includes(n.path));
  const moreOn = moreItems.some((n) => isActive(n.path, route.path));
  return (
    <div className="shell">
      <TopBar />
      <nav className="rail" aria-label="Main">
        {railItems.map((n, i) => (
          <Fragment key={n.path}>
            {i === railMid && <RailAsk active={isActive('/ask', route.path)} />}
            <a href={`#${n.path}`} className={isActive(n.path, route.path) ? 'on' : ''} aria-current={isActive(n.path, route.path) ? 'page' : undefined}>
              <n.icon aria-hidden />
              <span>{n.label}</span>
            </a>
          </Fragment>
        ))}
        {railMore.length > 0 && (
          <button type="button" className={`rail-more${moreOn ? ' on' : ''}`} onClick={() => setMore(true)} aria-current={moreOn ? 'page' : undefined}>
            <Menu aria-hidden />
            <span>More</span>
          </button>
        )}
      </nav>
      <main className="main" id="main">
        <NotSavedWarning />
        <UpdateBanner />
        <AppUpdate />
        {children}
      </main>
      <nav className="bottombar" aria-label="Main">
        {PHONE_NAV.map((p) => NAV.find((n) => n.path === p)!).map((n, i) => (
          <Fragment key={n.path}>
            {i === 2 && <MicTab active={isActive('/ask', route.path)} />}
            <a href={`#${n.path}`} className={isActive(n.path, route.path) ? 'on' : ''}>
              <n.icon aria-hidden />
              <span>{n.label}</span>
            </a>
          </Fragment>
        ))}
        <button type="button" onClick={() => setMore(true)} className={moreOn ? 'on' : ''}>
          <Menu aria-hidden />
          <span>More</span>
        </button>
      </nav>
      <Sheet open={more} onClose={() => (setMore(false), setEditing(false))} title={editing ? 'Your bar' : 'More'}>
        {editing ? (
          <EditBar nav={NAV} bar={PHONE_NAV} custom={custom} onDone={() => setEditing(false)} />
        ) : (
          <div className="more-grid">
            {moreItems.map((n) => (
              <a key={n.path} href={`#${n.path}`} onClick={() => setMore(false)} className={`more-item${isActive(n.path, route.path) ? ' on' : ''}`}>
                <n.icon aria-hidden />
                <span>{n.label}</span>
              </a>
            ))}
            {canGetApp(d) && (
              <a href="#/settings/widgets" onClick={() => setMore(false)} className="more-item">
                <Smartphone aria-hidden />
                <span>Get the app</span>
              </a>
            )}
            <a href="#/emergency" onClick={() => setMore(false)} className="more-item danger">
              <Siren aria-hidden />
              <span>Emergency</span>
            </a>
            {isPhone && !d.guest && (
              <button type="button" className="more-edit link small" onClick={() => setEditing(true)}>
                <Pin aria-hidden className="inline-icon" /> Choose what’s on the bar
              </button>
            )}
          </div>
        )}
      </Sheet>
    </div>
  );
}

/** Pick the three pages on the phone's bottom bar (Ask and More always stay). */
function EditBar({ nav, bar, custom, onDone }: { nav: NavItem[]; bar: string[]; custom: boolean; onDone: () => void }) {
  const [picked, setPicked] = useState<string[]>(bar);
  const toggle = (path: string) =>
    setPicked((p) => (p.includes(path) ? p.filter((x) => x !== path) : p.length < BAR_SIZE ? [...p, path] : [...p.slice(1), path]));
  const save = () => {
    if (picked.length === BAR_SIZE) setDevicePrefs({ tabs: picked });
    onDone();
  };
  return (
    <div className="edit-bar">
      <p className="muted small">Pick {BAR_SIZE}, in the order you want them. Ask and More always stay. This is just for this phone.</p>
      <div className="more-grid">
        {nav
          .filter((n) => n.path !== '/ask')
          .map((n) => {
            const at = picked.indexOf(n.path);
            return (
              <button key={n.path} type="button" className={`more-item${at >= 0 ? ' on' : ''}`} aria-pressed={at >= 0} onClick={() => toggle(n.path)}>
                {at >= 0 && <span className="more-pick">{at + 1}</span>}
                <n.icon aria-hidden />
                <span>{n.label}</span>
              </button>
            );
          })}
      </div>
      <div className="row gap edit-bar-actions">
        <button type="button" className="btn btn-primary btn-md" disabled={picked.length !== BAR_SIZE} onClick={save}>
          <Check aria-hidden /> {picked.length === BAR_SIZE ? 'Done' : `Pick ${BAR_SIZE - picked.length} more`}
        </button>
        {custom && (
          <button type="button" className="link small" onClick={() => (setDevicePrefs({ tabs: undefined }), onDone())}>
            Back to the suggested ones
          </button>
        )}
      </div>
    </div>
  );
}

function RailAsk({ active }: { active: boolean }) {
  return (
    <button type="button" className={`rail-ask${active ? ' on' : ''}`} onClick={() => openVoice({ listen: true })} aria-label="Talk to Arlo HQ">
      <span className="rail-ask-orb">
        <Mic aria-hidden />
      </span>
      <span>Ask</span>
    </button>
  );
}

function MicTab({ active }: { active: boolean }) {
  return (
    <button type="button" className={`mic-tab${active ? ' on' : ''}`} onClick={() => openVoice({ listen: true })} aria-label="Talk to Arlo HQ">
      <span className="mic-tab-orb">
        <Mic aria-hidden />
      </span>
      <span>Ask</span>
    </button>
  );
}

/** Is Sonos set up on the server? (Asked once; shows the music button on the tablet.) */
function useSonosOn(): boolean {
  const key = useDevice().key;
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!key) return;
    api<{ configured: boolean }>('/api/sonos?lite=1')
      .then((r) => setOn(r.configured))
      .catch(() => setOn(false));
  }, [key]);
  return on;
}

function TopBar() {
  const profile = useProfile();
  const sonos = useSonosOn();
  const kiosk = useDevice().kiosk;
  const status = useLiveStatus(60_000);
  const now = useNow(15_000);
  const stage = status.age ? stageFor(status.age.weeks) : undefined;
  const forecast = useWeather();
  const weatherNow = forecast?.hours.find((h) => h.time <= now && h.time > now - 3_600_000) ?? forecast?.hours.find((h) => h.time > now);
  return (
    <header className="topbar">
      <a href="#/" className="topbar-arlo" aria-label="Today">
        <span className="arlo-avatar">{profile.photo ? <img src={profile.photo} alt="" /> : <img src="./arlo.jpg" alt="" />}</span>
        <span className="topbar-name">
          <strong>{profile.name}</strong>
          <span className="muted small">
            {status.age ? (
              <>
                {status.age.label}
                {stage && <span className="topbar-stage"> · {stage.title}</span>}
              </>
            ) : (
              profile.breed
            )}
          </span>
        </span>
      </a>
      <div className="topbar-clock" aria-hidden>
        <span className="clock-time">{new Date(now).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
        <span className="clock-date">
          {new Date(now).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          {weatherNow && (
            <>
              {` · ${Math.round(weatherNow.tempC)}°`}
              {/* Squeezed (portrait tablet): just the temperature and rain, on one line. */}
              <span className="clock-cond"> {describeCode(weatherNow.code).toLowerCase()}</span>
              {weatherNow.rainChance >= 40 && ` · ${weatherNow.rainChance}% rain`}
            </>
          )}
        </span>
      </div>
      <div className="topbar-right">
        {kiosk && sonos && (
          <a href="#/music" className="power-btn" aria-label="Music" title="Music">
            <Music2 aria-hidden />
          </a>
        )}
        {kiosk && (
          <button type="button" className="power-btn" onClick={() => setScreenOff(true)} aria-label="Turn the screen off" title="Screen off">
            <Power aria-hidden />
          </button>
        )}
        <SyncDot />
        <PersonSwitch />
        <button type="button" className="sos" onClick={() => navigate('/emergency')}>
          <Siren aria-hidden />
          <span>Emergency</span>
        </button>
      </div>
    </header>
  );
}

/** A server exists but this device isn't saving to it (no key, or a rejected one). */
function useNotSaved(): boolean {
  const s = useSyncState();
  const d = useDevice();
  return !d.guest && (s.kind === 'needs-key' || s.kind === 'error');
}

/**
 * Loud, on every screen while a device isn't saving to the server — with the
 * fix right there (key, or Google on a phone), not a trip to Settings.
 */
function NotSavedWarning() {
  const s = useSyncState();
  const d = useDevice();
  const route = useRoute();
  const notSaved = useNotSaved();
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!notSaved || route.path.startsWith('/settings/sync')) return null;
  const thing = d.kiosk ? 'tablet' : 'device';
  const title =
    s.kind === 'error'
      ? isSession(d.key)
        ? 'Your Google sign-in has ended — sign in again'
        : `This ${thing}’s household key isn’t working`
      : `This ${thing} isn’t saving to the server`;
  const connect = async () => {
    if (!key.trim()) return setError('Type the household key first.');
    setBusy(true);
    const result = await checkKey(key);
    setBusy(false);
    if (result === 'wrong') return setError('That key wasn’t accepted — check for typos (it’s case-sensitive, and spaces count).');
    if (result === 'guest') return setError('That’s a sitter link, not the household key.');
    if (result === 'no-server') return setError('Couldn’t reach the server — check the internet connection.');
    setError('');
    setDevicePrefs({ key: key.trim() });
    await reconnect();
  };
  return (
    <div className="callout tone-bad not-saved" role="alert">
      <CloudOff aria-hidden />
      <div>
        <strong>{title}</strong>
        <span className="small">What’s logged here is only on this {thing} until it’s connected — then it all uploads straight away.</span>
        <form
          className="not-saved-form"
          onSubmit={(e) => {
            e.preventDefault();
            void connect();
          }}
        >
          <input className="input" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Household key" aria-label="Household key" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          <button type="submit" className="btn btn-primary btn-md" disabled={busy}>
            <span>{busy ? 'Checking…' : 'Connect'}</span>
          </button>
        </form>
        {error && <span className="small not-saved-error">{error}</span>}
        {!d.kiosk && <GoogleSignIn lead="Or:" />}
        {d.kiosk && (
          <span className="small muted">
            So it stays connected after a reset, put the key in Scalefusion’s launch URL too: <code>{`${location.origin}/?kiosk=1&key=YOUR-KEY`}</code>
          </span>
        )}
      </div>
    </div>
  );
}

/** Phones and laptops: a quiet nudge when a newer version has been deployed. */
function UpdateBanner() {
  const { stale } = useServerBuild();
  const d = useDevice();
  if (!stale || d.kiosk) return null;
  return (
    <button type="button" className="callout tone-info update-banner" onClick={() => void reloadToLatest()}>
      <RefreshCw aria-hidden />
      <div>
        <strong>A new version of Arlo HQ is ready</strong>
        <span className="small">Tap to update — takes a second.</span>
      </div>
    </button>
  );
}

function SyncDot() {
  const s = useSyncState();
  // The red banner says it (and fixes it); a second, vaguer badge only confuses.
  if (useNotSaved()) return null;
  let icon = Cloud;
  let label = 'Synced';
  let tone = 'good';
  if (s.kind === 'local-only') {
    icon = CloudOff;
    label = 'This device only';
    tone = 'neutral';
  } else if (s.kind === 'needs-key') {
    icon = CloudOff;
    label = 'Connect in Settings';
    tone = 'warn';
  } else if (s.kind === 'connecting') {
    icon = RefreshCw;
    label = 'Connecting';
    tone = 'neutral';
  } else if (s.kind === 'offline') {
    icon = CloudOff;
    label = s.pending ? `Offline · ${s.pending} to send` : 'Offline';
    tone = 'warn';
  } else if (s.kind === 'error') {
    icon = CloudOff;
    label = 'Sync problem';
    tone = 'bad';
  }
  const Icon = icon;
  return (
    <a href="#/settings/sync" className={`sync-dot tone-${tone}`} title={label} aria-label={`Sync: ${label}`}>
      <Icon aria-hidden />
      <span className="sync-label">{label}</span>
    </a>
  );
}

/** Tap to switch who is logging on this device. */
function PersonSwitch() {
  const household = useHousehold();
  const device = useDevice();
  const people = household.people;
  const presenceRecords = useCollection('presence');
  const presence = new Map(presenceRecords.map((r) => [r.id, r.state]));
  return (
    <div className="person-switch" role="radiogroup" aria-label="Who is logging">
      {people.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={device.personId === p.id}
          className={device.personId === p.id ? 'on' : ''}
          onClick={() => setDevicePrefs({ personId: device.personId === p.id ? undefined : p.id })}
          title={`Logging as ${p.name}`}
        >
          <span className="avatar-wrap">
            <Avatar person={p} size={34} />
            {presence.get(p.id) && <i className={`presence-dot ${presence.get(p.id)}`} title={presence.get(p.id) === 'home' ? `${p.name} is home` : `${p.name} is out`} />}
          </span>
          <span className="person-name">{p.name}</span>
        </button>
      ))}
    </div>
  );
}
